import * as moment from 'moment';
import * as _ from 'lodash';
import { Component, OnInit } from '@angular/core';
import { OverlayService } from '../../../services/overlay.service';
import { LogService } from '../../../services/log.service';
import { OverlayRecord } from '../../../types/overlay/overlay-record';
import { SpinnerService } from '../../../services/spinner.service';
import { ToasterService } from '../../../services/toaster.service';
import { NgbDropdownConfig } from '@ng-bootstrap/ng-bootstrap';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-overlay-home',
  templateUrl: './overlay-home.component.html',
  styleUrls: ['./overlay-home.component.scss']
})
export class OverlayHomeComponent implements OnInit {
  private LOCAL_STORAGE_KEY = 'overlayApiParams';
  objectKeys = Object.keys;

  records: OverlayRecord[] = [];
  filteredRecords: OverlayRecord[] = [];
  selectAllToggle = false;
  projectedAction = '';

  apiParams: any = {
    recordCountToLoad: 1000,
    fromDate: "",
    toDate: "",
    programs: "",
    display: {
      create: {
        option: 'Create Date',
        value: true
      },
      action: {
        option: 'Action Date',
        value: true
      },
      payers: {
        option: 'Current Payer Memberships',
        value: true
      },
      name: {
        option: 'First and Last Name',
        value: true
      },
      dob: {
        option: 'DOB',
        value: true
      },
      gender: {
        option: 'Gender',
        value: true
      },
      phone: {
        option: 'Phone',
        value: true
      },
      address: {
        option: 'Address',
        value: true
      }
    }
  };

  filters: any;

  constructor(private log: LogService,
              private spinner: SpinnerService,
              private toast: ToasterService,
              private overlayService: OverlayService,
              config: NgbDropdownConfig) {
    config.placement = 'bottom-right';
    config.autoClose = true;

    this.initFilters();

    this.spinner.showSpinner();
    let storedApiParams = window.localStorage.getItem(this.LOCAL_STORAGE_KEY);

    try {
      storedApiParams = JSON.parse(storedApiParams);
    } catch {
      storedApiParams = undefined;
    }

    if (!storedApiParams) {
      this.storeApiParams();
    }
    else {
      this.apiParams = storedApiParams;
    }
  }

  ngOnInit() {
    this.refreshList();
  }

  initFilters() {
    this.filters = {
      Facilities: {},
      Payers: {}
    };
  }

  private handleError = err => {
    const response = _.get(err, 'error', err);
    this.log.error(response);
    this.spinner.hideSpinner();
    this.toast.showAlert(
      _.get(
        response,
        'error',
        _.get(response, 'message', JSON.stringify('Overlays are not retrived for the specified date range. Please change the filter settings and try again: '+response))
      )
    );
  };

  refreshList() {
      this.spinner.showSpinner();
      this.overlayService.getOverlays(this.apiParams.recordCountToLoad,this.apiParams.fromDate,this.apiParams.toDate,this.apiParams.programs).pipe(
        map((overlays: OverlayRecord[]) => {
          overlays.forEach(overlay => OverlayRecord.addSpaceIfBlank(overlay));
          return overlays;
        })
      ).subscribe(overlays => {
        this.log.trace('OverlayHome.GetOverlays', overlays);
        this.records = overlays;
        this.initFilters();
        this.filteredRecords = this.records;
        this.spinner.hideSpinner();
      }, this.handleError);
  }

  ignoreRecords() {
    const overlays = this.getSelectedOverlays();
    this.spinner.showSpinner();
    this.overlayService.ignoreRecords(overlays).subscribe(
      response => {
        this.spinner.hideSpinner();
        this.toast.showInfoTimed(`${overlays.length} record${overlays.length === 1 ? '' : 's'} successfully ignored.`);
        _.pullAllWith(this.records, overlays, (a, b) => a.ID === b.ID);
        this.applyFilter();
      },
      response => {
        this.spinner.hideSpinner();
        this.toast.showAlert(_.get(response.error, 'error', response.error));
      }
    );
  }

  acceptRecords() {
    const overlays = this.getSelectedOverlays();
    this.spinner.showSpinner();
    this.overlayService.acceptRecords(overlays).subscribe(
      response => {
        this.spinner.hideSpinner();
        this.toast.showInfoTimed(`${overlays.length} record${overlays.length === 1 ? '' : 's'} successfully allowed.`);
        _.pullAllWith(this.records, overlays, (a, b) => a.ID === b.ID);
        this.applyFilter();
      },
      response => {
        this.spinner.hideSpinner();
        this.toast.showAlert(_.get(response.error, 'error', response.error));
      }
    );
  }

  storeApiParams() {
    window.localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(this.apiParams));
  }

  setAction(action: string) {
    this.projectedAction = action;
  }

  selectAllToggleChanged(state: boolean) {
    this.log.trace('OverlayHome.SelectAllToggleChanged', state);
    this.selectAllToggle = state;
    this.filteredRecords.forEach(record => record.Selected = state);
  }

  selectToggle(record: OverlayRecord) {
    this.log.trace('OverlayHome.SelectToggle', !record.Selected);
    this.filterSelectedChanged(record, !record.Selected);
  }

  getSelectedCount() {
    return _.filter(this.filteredRecords, record => record.Selected).length;
  }

  getSelectedOverlays() {
    return _.chain(this.filteredRecords)
      .filter(record => record.Selected)
      .map((record: OverlayRecord) => { return { ID: record.ID } })
      .value();
  }

  showColumn(columnName: string) {
    return this.apiParams.display[columnName].value;
  }

  ignoreRowClick(event: MouseEvent) {
    event.cancelBubble = true;
  }

  filterSelectedChanged(record: OverlayRecord, state: boolean) {
    this.log.trace('OverlayHome.FilterSelectedChanged', state);
    record.Selected = state;
    if (!state && this.selectAllToggle) {
      this.selectAllToggle = false;
    }
    else if (state && _.every(this.filteredRecords, filteredRecord => filteredRecord.Selected)) {
      this.selectAllToggle = true;
    }
  }

  highlight(record: OverlayRecord, property: string): string {
    const hsValue = _.get(record, `${property}_HS`, ''),
      veratoValue = _.get(record, `${property}_Verato`, ''),
      highlight = hsValue !== veratoValue;
    return highlight ? 'highlight' : '';
  }

  getDateInputClass(property: string) {
    if (!!this.filters[property]) {
      return '';
    }
    return ' empty';
  }

  getFacilities() {
    return _.chain(this.records)
      .map(record => record.Facility)
      .sortBy(_.identity)
      .uniq()
      .value();
  }

  getPayers() {
    return _.chain(this.records)
      .map(record => record.HealthPlans)
      .flatten()
      .compact()
      .sortBy(_.identity)
      .uniq()
      .value();
  }

  displayArray(array?: string[]) {
    return array
      ? array.join('<br/>')
      : '';
  }

  clearFilter(property: string) {
    this.filters[property] = '';
    this.applyFilter();
  }

  clearFacilityFilters() {
    this.filters.Facilities = {};
    this.applyFilter();
  }

  clearPayerFilters() {
    this.filters.Payers = {};
    this.applyFilter();
  }

  hasFiltersApplied() {
    let hasFilters = false;
    _.forOwn(this.filters, (value, key) => {
      if (key === 'Facilities') {
        if (!_.isEmpty(_.pickBy(value, val => !!val))) {
          hasFilters = true;
          return true;
        }
      }
      else if (key === 'Payers') {
        if (!_.isEmpty(_.pickBy(value, val => !!val))) {
          hasFilters = true;
          return true;
        }
      }
      else if (!!value) {
        hasFilters = true;
        return true;
      }
    });
    return hasFilters;
  }

  applyFilter() {
    this.filteredRecords = this.records;
    _.forOwn(this.filters, (value, key) => {
      if (!!value) {
        if (key === 'Facilities') {
          let facilities = this.filters.Facilities;
          facilities = _.compact(_.map(facilities, (selected, facility) => selected && facility));
          if (facilities.length) {
            this.filteredRecords = _.filter(this.filteredRecords, record => {
              return _.includes(facilities, record.Facility);
            });
          }
        }
        else if (key === 'Payers') {
          let payers = this.filters.Payers;
          payers = _.compact(_.map(payers, (selected, payer) => selected && payer));
          if (payers.length) {
            this.filteredRecords = _.filter(this.filteredRecords, record => {
              return _.intersectionWith(payers, record.HealthPlans, _.isEqual).length > 0;
            });
          }
        }
        else {
          this.filteredRecords = _.filter(this.filteredRecords, record => {
            return _.includes(record[key], value);
          });
        }
      }
    });
  }

}
